import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { ApiError } from '../../../../services/api';
import { fetchCampaignsForAccount } from './campaigns.thunks';
import { fetchAccountsList } from '../../list.thunks';
import { fetchInvoices } from '../../../invoices/list/invoicesList.thunks';
import { BlockedByFeature, notAvailablePropExceptIn } from '../../../../featureToggles/features/utils';
import { ICampaign, ICampaignsState, InitialCampaignState } from './campaigns.interface';
import { Features } from '../../../../featureToggles/features';
import { RootState } from '../../../rootReducer';

// TODO: Remove this entire file when Features.BillingUserFacing is approved in staging and prod
export const initialState: InitialCampaignState = notAvailablePropExceptIn({
  expected: {
    error: null,
    loading: false,
    list: [],
    listPerAccount: []
  },
  availableIn: [Features.PlgExternal3]
});

const getCampaignsState = (state: Pick<RootState, 'accounts'>): ICampaignsState | undefined => {
  const campaignsState: InitialCampaignState = state.accounts.projects.campaigns;
  if (campaignsState === BlockedByFeature) {
    return;
  }
  return campaignsState;
};
const getCampaignList = (state: Pick<RootState, 'accounts'>): ICampaign[] => getCampaignsState(state)?.list || [];
const getAllCampaignsForAccount = (state: Pick<RootState, 'accounts'>): ICampaign[] => getCampaignsState(state)?.listPerAccount || [];
export const campaignsSelector = { getCampaignList, getAllCampaignsForAccount };

const reducers = {
  [fetchCampaignsForAccount.pending.type]: (state: InitialCampaignState): InitialCampaignState => {
    if (state === BlockedByFeature) {
      return state;
    }
    state.loading = true;
    state.error = null;
    return state;
  },
  [fetchCampaignsForAccount.fulfilled.type]: (state: InitialCampaignState, action: PayloadAction<ICampaign[]>): InitialCampaignState => {
    const campaigns: ICampaign[] = action.payload ?? [];
    if (state === BlockedByFeature) {
      return state;
    }
    state.loading = false;
    state.listPerAccount = campaigns;
    return state;
  },
  [fetchCampaignsForAccount.rejected.type]: (state: InitialCampaignState, action: PayloadAction<ApiError | null>): InitialCampaignState => {
    if (state === BlockedByFeature) {
      return state;
    }
    state.loading = false;
    state.error = action.payload;
    return state;
  },
  [fetchAccountsList.pending.type]: (state: InitialCampaignState): InitialCampaignState => {
    if (state === BlockedByFeature) {
      return state;
    }
    state.loading = false;
    state.error = null;
    state.list = [];
    state.listPerAccount = [];
    return state;
  },
  [fetchInvoices.fulfilled.type]: (state: InitialCampaignState): InitialCampaignState => {
    if (state === BlockedByFeature) {
      return state;
    }
    state.loading = false;
    state.error = null;
    state.list = [];
    state.listPerAccount = [];
    return state;
  }
};

const campaignsForProject = createSlice({
  name: 'campaigns',
  initialState,
  extraReducers: reducers,
  reducers: {
    clearCampaigns: (state: InitialCampaignState) => {
      if (state === BlockedByFeature) {
        return state;
      }
      state = {
        error: null,
        loading: false,
        list: [],
        listPerAccount: state.listPerAccount
      };
      return state;
    },
    setCampaignsForProjectId: (state: InitialCampaignState, action: PayloadAction<string>) => {
      if (state === BlockedByFeature) {
        return state;
      }
      state.list = state.listPerAccount.filter((campaign) => campaign.projectId === action.payload);
      return state;
    }
  }
});

export default campaignsForProject.reducer;
export const actions = {
  ...campaignsForProject.actions
};
